import liCSS from 'components/Shared/MakeModelMenu/liCSS';
import { colors, fonts } from 'components/styles/variables';
import searchIcon from 'public/static/images/paid_landing/search-icon.svg';
import carIcon from 'public/static/images/car-icon.svg';
import makeModelBackArrow from 'public/static/images/make-model-back-arrow.svg';
import { MouseEvent, useContext, useMemo } from 'react';
import useEvents from 'lib/hooks/useEvents';
import { FilterMakesTap, FilterModelsTap } from 'lib/Tracking/MakeModelMenu/MakeModelMenuEvents';
import { FilterEventContext } from 'contexts/FilterEventContext';
import DeviceScreen from 'lib/DeviceScreen';
import { useMultiMakeModelFilter } from 'lib/hooks/useSrpFilters';
import { ChooseFunction } from './types';

interface MakeModelFilterProps {
  autoFocus?: boolean;
  handleFilter: (filter: string) => void;
  makeOrModel?: string;
  showMakes?: (event: MouseEvent) => void;
}

interface MakeModelSelectAllProps {
  choose?: ChooseFunction;
  text?: string;
  makeOrModel?: string;
  make?: string;
  filterIndex?: number;
}

interface MakeModelHeaderProps {
  handleFilter: (filter: string) => void;
  includeAll?: boolean;
  chooseAll?: ChooseFunction;
  allText?: string;
  makeOrModel?: string;
  showMakes?: (event: MouseEvent) => void;
  make?: string;
  filterIndex?: number;
}

export function MakeModelFilter({
  autoFocus = !DeviceScreen.isMobile(),
  handleFilter,
  makeOrModel,
  showMakes,
}: MakeModelFilterProps): JSX.Element {
  const logger = useEvents();
  const filterEventContext = useContext(FilterEventContext);

  function handleClick(event: MouseEvent): void {
    if (makeOrModel === 'make') logger.log(FilterMakesTap(filterEventContext));
    else if (makeOrModel === 'model') logger.log(FilterModelsTap(filterEventContext));
    event.stopPropagation();
  }

  function handleChange(event: { target: { value: string } }): void {
    const { value } = event.target;

    if (value === undefined) {
      return;
    }

    handleFilter(value);
  }

  return (
    <div className="filter-container">
      <div className="input-container">
        {makeOrModel === 'make' ? (
          <span className="search-icon-container">
            <div className="search-icon" />
          </span>
        ) : (
          <span className="back-arrow-container">
            <button aria-label="back-arrow" className="back-arrow" onClick={showMakes} />
          </span>
        )}
        <input
          autoFocus={autoFocus}
          data-testid="make-model-menu-filter-input"
          type="text"
          placeholder={`Search ${makeOrModel}`}
          onClick={handleClick}
          onChange={handleChange}
        />
      </div>
      <style jsx>{liCSS}</style>

      <style jsx>
        {`
          .filter-container {
            position: sticky;
            z-index: 1;
            top: 0;
            background: white;
          }

          .input-container {
            position: relative;
            display: flex;
            align-items: center;
            padding: 0 16px;
            box-shadow: 0 1px 0 0 ${colors.G2};
          }

          .search-icon-container {
            z-index: 1;
            height: 22px;
            width: 22px;
            display: flex;
            justify-content: center;
            align-items: center;
            margin-left: 8px;
            margin-right: 9px;
          }

          .search-icon {
            height: 22px;
            width: 21px;
            background-image: url('${searchIcon}');
            background-size: contain;
            background-repeat: no-repeat;
          }

          .back-arrow-container {
            z-index: 1;
            height: 20px;
            width: 20px;
            display: flex;
            justify-content: center;
            align-items: center;
            margin-left: 7px;
            margin-right: 12px;
          }

          .back-arrow {
            background-image: url('${makeModelBackArrow}');
            background-size: contain;
            background-repeat: no-repeat;
            height: 14px;
            width: 19px;
            cursor: pointer;
            background-color: transparent;
            color: inherit;
            border: none;
            padding: 0;
            font: inherit;
            cursor: pointer;
            outline: inherit;
          }

          input {
            background-color: transparent;
            border: none;
            height: 56px;
            width: 100%;
            font-size: 16px;
            line-height: 20px;
            color: ${colors.BLACK};
            padding: 8px;
            -webkit-appearance: none;
            border-radius: 8px;
            outline: none;
          }

          input ::placeholder {
            color: ${colors.G3};
            font-size: 16px;
            font-family: ${fonts.default};
            line-height: 24px;
          }
        `}
      </style>
    </div>
  );
}

function MakeModelSelectAll({
  choose,
  text,
  makeOrModel,
  make,
  filterIndex,
}: MakeModelSelectAllProps): JSX.Element {
  const { value } = useMultiMakeModelFilter();

  const selectedMakes = useMemo(() => {
    if (!value) return [];

    return value.reduce((uniqueMakes: string[], item) => {
      if (item && item.make && !uniqueMakes.includes(item.make)) {
        uniqueMakes.push(item.make);
      }
      return uniqueMakes;
    }, []);
  }, [value]);

  const isCurrentMakeSelected = useMemo(() => {
    if (!make || filterIndex === undefined) return false;
    return selectedMakes[filterIndex - 1] === make;
  }, [make, selectedMakes, filterIndex]);

  function onClick(): void {
    if (!isCurrentMakeSelected) {
      choose?.();
    }
  }

  return (
    <li
      className={`all-container make-model-menu-makes-list-item ${
        isCurrentMakeSelected ? 'disabled' : ''
      }`}
      data-make-all
      data-testid="make-model-select-all"
      onClick={onClick}
    >
      {makeOrModel === 'make' ? <div className="car-icon" /> : undefined}
      <div>{text}</div>
      <style jsx>{liCSS}</style>
      <style jsx>{`
        .all-container {
          display: flex;
          align-items: center;
          padding: ${makeOrModel === 'make' ? '0 16px' : '0 24px'};
          font-size: 14px;
          line-height: 20px;
          letter-spacing: 0.2px;
        }
        .all-container.disabled {
          color: ${colors.G3};
        }
        .all-container.disabled:hover {
          cursor: default;
        }
        .car-icon {
          background-image: url('${carIcon}');
          background-repeat: no-repeat;
          height: 38px;
          width: 38px;
          margin-right: 12px;
        }
      `}</style>
    </li>
  );
}

function MakeModelHeader({
  handleFilter,
  includeAll,
  chooseAll,
  allText,
  makeOrModel,
  showMakes,
  make,
  filterIndex,
}: MakeModelHeaderProps): JSX.Element {
  return (
    <>
      <MakeModelFilter
        handleFilter={handleFilter}
        makeOrModel={makeOrModel}
        showMakes={showMakes}
      />
      {includeAll ? (
        <MakeModelSelectAll
          makeOrModel={makeOrModel}
          choose={chooseAll}
          text={allText}
          make={make}
          filterIndex={filterIndex}
        />
      ) : null}
    </>
  );
}

export default MakeModelHeader;
